import React, { useState, useCallback } from 'react';
import {
  BaseButton,
  ButtonGroup,
  ErrorHandler,
  Drawer,
  PrimaryButton,
} from '@common/components';
import { Form } from 'antd';
import { createMessage } from 'works/legacy/lib/message-helper';
import { useMutation, useQuery } from '@common/hooks';
import {
  updateOrganizationBusinessMutation,
  UpdateOrganizationBusinessMutationVariables,
  UpdateOrganizationBusinessMutationResponse,
  organizationBusinessQuery,
  OrganizationBusinessQueryVariables,
  OrganizationBusinessQueryResponse,
} from '@institutions/graphql';
import {
  buildOrganizationBusiness,
  OrganizationFacilityForm,
  FacilityFormValues,
} from '@institutions/components/organization-facility-form';

const errorToast = () =>
  createMessage('error', 'Something went wrong. Try again later.');

export function OrganizationFacilityEditDrawer({
  open,
  onClose,
  facilityId,
}: {
  open: boolean;
  onClose: () => void;
  facilityId?: string;
}) {
  const [form] = Form.useForm();
  const [isSubmitButtonEnabled, setIsSubmitButtonEnabled] = useState(false);
  const {
    data: organizationBusinessQueryData,
    loading: organizationBusinessQueryLoading,
    error: organizationBusinessQueryError,
    refetch: refetchOrganizationFacilityQuery,
  } = useQuery<
    OrganizationBusinessQueryResponse,
    OrganizationBusinessQueryVariables
  >(organizationBusinessQuery, {
    skip: !facilityId,
    variables: {
      id: facilityId || '',
    },
  });

  const cancelDrawer = () => {
    form.resetFields();
    setIsSubmitButtonEnabled(false);
    onClose();
  };

  const initialValues = organizationBusinessQueryData?.organizationBusiness
    ? buildOrganizationBusiness(
        organizationBusinessQueryData.organizationBusiness
      )
    : {};

  const [save, { loading: mutationLoading }] = useMutation<
    UpdateOrganizationBusinessMutationResponse,
    UpdateOrganizationBusinessMutationVariables
  >(updateOrganizationBusinessMutation);

  const handleSubmitButtonEnabled = useCallback(() => {
    const formData = Object.values(
      buildOrganizationBusiness(form.getFieldsValue())
    );

    const hasAnyDifferentData = Object.values(initialValues).some(
      (initialValue, index) => {
        const formValue = formData[index];
        return initialValue !== formValue;
      }
    );

    setIsSubmitButtonEnabled(hasAnyDifferentData);

    form.validateFields().catch((e) => {
      if (e?.errorFields?.length) {
        setIsSubmitButtonEnabled(false);
      }
    });

    // @TODO: verify if initialValues is really missed as a dependency here, since adding it might cause side effects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, organizationBusinessQueryData]);

  function handleAlertOnSuccess() {
    setIsSubmitButtonEnabled(false);
    refetchOrganizationFacilityQuery();
    createMessage('success', 'Facility successfully updated.');
    onClose();
  }

  async function handleSave(payload: unknown) {
    try {
      const response = await save({
        variables: {
          id: facilityId || '',
          input: buildOrganizationBusiness(payload as FacilityFormValues),
        },
      });

      if (response?.errors?.length) {
        const messages = response?.errors?.map(({ message }) => message);
        createMessage('error', messages?.join(', '));
      } else {
        handleAlertOnSuccess();
      }
    } catch {
      errorToast();
    }
  }

  if (organizationBusinessQueryError) {
    return (
      <ErrorHandler
        error={organizationBusinessQueryError}
        log="OrganizationFacilityEditDrawer"
      />
    );
  }

  const footer = (
    <ButtonGroup>
      <BaseButton size="large" onClick={cancelDrawer}>
        Cancel
      </BaseButton>
      <PrimaryButton
        size="large"
        onClick={form.submit}
        loading={mutationLoading}
        disabled={!isSubmitButtonEnabled}
      >
        Save
      </PrimaryButton>
    </ButtonGroup>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      className="organization-facility-edit-drawer"
      title="Edit Facility Details"
      footer={footer}
      loading={organizationBusinessQueryLoading}
    >
      <OrganizationFacilityForm
        form={form}
        onFinish={handleSave}
        initialValues={initialValues}
        onValuesChange={handleSubmitButtonEnabled}
      />
    </Drawer>
  );
}
